'use client'

import * as React from 'react'
import { useState } from 'react'
import Link from 'next/link'
import { createClient } from '@/lib/supabase-browser'
import { Spinner } from '@/components/ui/spinner'

type State = 'idle' | 'loading' | 'sent'

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [state, setState] = useState<State>('idle')
  const supabase = createClient()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setState('loading')

    // Always show the same success state, regardless of whether the email
    // is registered or whether Supabase succeeded. Two reasons:
    //   1. Don't leak which addresses have accounts.
    //   2. Supabase's resetPasswordForEmail has a 60s built-in throttle that
    //      returns an error on rapid retries — surfacing that as a UI error
    //      would be more confusing than helpful.
    await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback?next=/reset-password`,
    })

    setState('sent')
  }

  return (
    <main className="min-h-screen bg-cream flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <Link href="/" className="inline-flex items-baseline gap-1.5">
            <span lang="zh-Hant" translate="no" className="font-serif-zh text-2xl font-semibold text-gold tracking-wide">寶話</span>
            <span className="font-serif text-sm uppercase tracking-widest text-ink">Stories</span>
          </Link>
        </div>

        {state === 'sent' ? (
          <div className="border border-gold/30 bg-gold/5 px-6 py-6 text-center mb-8">
            <p className="font-serif text-sm text-ink mb-2">
              If an account exists for <strong>{email}</strong>, we sent a reset link.
            </p>
            <p className="font-serif text-sm text-text-secondary">
              Check your email and click the link to set a new password.
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-serif text-2xl text-ink text-center mb-2">
              Forgot your password?
            </h1>
            <p className="font-serif text-sm text-text-secondary text-center mb-8">
              Enter your email and we&apos;ll send you a link to reset it.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="forgot-email" className="block font-serif text-xs uppercase tracking-widest text-gold mb-2">
                  Email
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full bg-transparent border border-subtle px-4 py-3 font-serif text-ink focus:outline-none focus:border-gold transition-colors"
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={state === 'loading' || !email}
                className="btn-primary w-full disabled:opacity-50"
              >
                {state === 'loading' ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <Spinner className="w-4 h-4 border" />
                    Sending...
                  </span>
                ) : (
                  'Send reset link'
                )}
              </button>
            </form>
          </>
        )}

        <p className="text-center mt-8 font-serif text-sm text-text-muted">
          Remembered it?{' '}
          <Link href="/login" className="text-gold hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </main>
  )
}
